import cx from "classnames";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { FormFieldStates, InputProps } from "./exports";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { FC, PropsWithChildren, useMemo, useState } from "react";

export type QESTextAreaProps = InputProps & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

/**
 * A standard text area with a title and optional descriptive text beneath. Its props include all native HTML TextArea attributes, and in particular, adding a maxLength attribute will add a character count underneath.
 */
/* We call out the custom props here individually, so ...props is just the normal HTML input attributes */
export const QESTextArea: FC<PropsWithChildren<QESTextAreaProps>> = (props: QESTextAreaProps) => {
  const { containerClassName,
    descriptiveText,
    errorText,
    hideLabel,
    inlineLabel,
    isValid,
    label,
    labelWidth,
    maxLength,
    qesRef,
    required,
    testId,
    validationTooltipContent,
    unrequiredValidation,
    wasFocusedExternally,
    ...restProps
  } = props;
  const [hasBeenFocused, setHasBeenFocused] = useState(wasFocusedExternally);

  const fieldState = useMemo(() => {
    if (!isValid && (required || unrequiredValidation) && (hasBeenFocused || wasFocusedExternally)) {
      return FormFieldStates.Error;
    }
    return FormFieldStates.None;
  }, [isValid, required, unrequiredValidation, hasBeenFocused, wasFocusedExternally]);

  const subText = useMemo(() => {
    if ((hasBeenFocused || wasFocusedExternally) && errorText && fieldState === FormFieldStates.Error) {
      return errorText;
    } else if (descriptiveText) {
      return descriptiveText;
    }
    return "";
  }, [descriptiveText, errorText, fieldState, hasBeenFocused, wasFocusedExternally]);

  const characterCount = useMemo(() => {
    return (props.value as string)?.length || 0;
  }, [props.value]);

  const isShowingError = (hasBeenFocused || wasFocusedExternally) && fieldState === FormFieldStates.Error;

  return (
    // onChange bubbles up when triggered. So changing the textarea is caught by .form-groups onChange,
    // without negating any onChange passed in to the textarea by ...props
    <div
      className={cx({
        [containerClassName]: !!containerClassName,
        "inline-label": inlineLabel,
        "qes-text-area__container": true,
      })}
      key={testId}
      onBlur={() => setHasBeenFocused(true)}
    >
      <label
        htmlFor={props.id || testId}
        className={cx({
          "qes-text-area__label": true,
          "visually-hidden-label": hideLabel,
        })}
        style={{ width: labelWidth ?? "auto" }}
      >
        {label}
        {required || validationTooltipContent ? (
          <span className="qes-text-area__label-subcontent">
            {required ? <span className="text-red500"> *</span> : ""}
            {validationTooltipContent ? <TooltipWrapper containerClassName="qes-text-area__tooltip-container" text={validationTooltipContent}><QesIcon iconName={QesIcons.InfoCircle} description="Info" width="18" /></TooltipWrapper> : ""}
          </span>
        ) : null}
      </label>
      <div className="qes-text-area__subcontainer">
        <textarea
          {...restProps}
          id={props.id || testId}
          className={cx({
            "error": isShowingError,
            "qes-text-area__textarea": true,
            [props.className]: !!props.className,
          })}
          data-testid={testId}
          required={required}
          maxLength={maxLength}
          ref={qesRef as React.RefObject<HTMLTextAreaElement>}
        />
        <div className="">
          {subText && (
            <small className="qes-text-area__descriptive-text">
              {isShowingError && <QesIcon iconName={QesIcons.XCircle} isDecorative />}
              {subText}
            </small>
          )}
          {maxLength && (
            <small
              className={cx({
                "danger": characterCount >= maxLength,
                "qes-text-area__descriptive-text qes-text-area__character-count": true,
              })}
            >
              {(maxLength - characterCount).toLocaleString()} / {maxLength.toLocaleString()}
            </small>
          )}
        </div>
      </div>
    </div>
  );
};
